import { Link, useLocation } from "react-router-dom";
import { Star, ArrowUpRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

/**
 * NotFound — the firm's 404. Editorial, restrained, and pointing readers
 * back to the four most-visited destinations. Sets HTTP metadata via
 * react-helmet-async so crawlers register a proper 404 signal (noindex).
 */
export default function NotFound() {
  const location = useLocation();

  const DESTINATIONS = [
    {
      to: "/",
      no: "01",
      label: "Return to the front page",
      meta: "Home",
    },
    {
      to: "/practice",
      no: "02",
      label: "Browse the practice continuum",
      meta: "All Services",
    },
    {
      to: "/journal",
      no: "03",
      label: "Read the Journal",
      meta: "Editorial Archive",
    },
    {
      to: "/site-map",
      no: "04",
      label: "Open the directory",
      meta: "Site Map",
    },
  ];

  return (
    <div className="bg-ivory min-h-screen flex flex-col" data-testid="not-found-page">
      <Helmet>
        <title>Page Not Found &mdash; Lone Star Counsel</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="prerender-status-code" content="404" />
        <link
          rel="canonical"
          href="https://lonestarcounsel.com/site-map"
        />
      </Helmet>

      <section
        className="panel-navy grain grain-dark pt-40 pb-24 md:pt-48 md:pb-32 flex-1 relative overflow-hidden"
        data-testid="not-found-hero"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left rail — the editorial notice */}
            <div className="lg:col-span-7">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" />
                <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                  Notice from the Editor
                </div>
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.28em] text-ivory/60">
                Page 404 &middot; The Firm&apos;s Directory
              </div>

              <h1
                className="mt-10 font-display text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.94] text-ivory tracking-tight"
                data-testid="not-found-h1"
              >
                The page you&apos;re looking for is
                <br />
                <span className="italic font-cormorant text-brass">
                  out of print.
                </span>
              </h1>

              <p className="mt-10 max-w-2xl text-lg md:text-xl text-ivory/85 font-light leading-relaxed">
                It may have been retired, relocated to a new address, or perhaps
                never existed in the firm&apos;s archives at all. Whichever the
                case &mdash; below are four ways back into the counsel table.
              </p>

              {location.pathname && (
                <div
                  className="mt-8 font-cormorant italic text-ivory/50 text-base leading-snug"
                  data-testid="not-found-attempted-path"
                >
                  Attempted address:{" "}
                  <span className="text-brass/80">{location.pathname}</span>
                </div>
              )}
            </div>

            {/* Right rail — four hairline destinations */}
            <div
              className="lg:col-span-5 lg:pt-6"
              data-testid="not-found-destinations"
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium mb-6">
                Where to next
              </div>
              <ul>
                {DESTINATIONS.map((d) => (
                  <li
                    key={d.to}
                    className="border-t border-brass/25 first:border-t-0"
                    data-testid={`not-found-dest-${d.no}`}
                  >
                    <Link
                      to={d.to}
                      className="group flex items-baseline justify-between gap-4 py-5 hover:bg-ivory/5 transition-colors"
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="text-[10px] tracking-[0.32em] uppercase text-brass font-medium">
                          No. {d.no}
                        </span>
                        <span className="font-display text-ivory text-lg md:text-xl leading-snug group-hover:text-brass transition-colors">
                          {d.label}
                        </span>
                      </div>
                      <span className="flex items-center gap-3 shrink-0">
                        <span className="hidden md:inline text-[10px] uppercase tracking-[0.28em] text-ivory/50">
                          {d.meta}
                        </span>
                        <ArrowUpRight
                          size={13}
                          className="text-brass opacity-60 group-hover:opacity-100 transition-opacity"
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-10 hairline-top pt-6">
                <p className="font-cormorant italic text-ivory/70 text-base leading-snug">
                  Or, if you were searching for a particular piece of counsel &mdash;
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-brass hover:text-ivory transition-colors font-medium border-b border-brass/40 hover:border-ivory pb-1"
                  data-testid="not-found-contact"
                >
                  Write to the firm <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Colophon */}
      <div className="bg-ivory py-8 border-t border-brass/25">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            Pratt Law Group, PLLC d/b/a Lone Star Counsel &middot; Frisco, Texas
          </div>
          <div className="font-cormorant italic text-slate2 text-sm">
            Every retired page is a mark of a working archive.
          </div>
        </div>
      </div>
    </div>
  );
}
